// Tolerant readers for apartment_inspection_records.attempts[] elements.
// The JSONB array exists in TWO shapes in the wild (and a single array can MIX them
// after partial re-saves — see docs/PROCES-CEEB + migration 021):
//   canonical (021+): { termNumber:int, status:string|null, date:'YYYY-MM-DD', note:string|null }
//   legacy (pre-021): { attemptNumber:int, timestamp:ISO }
// These helpers read EITHER shape so renderers never emit "Próba /-",
// "Próba undefined" or "Invalid Date" (bugs W3/W4/W8).

export interface RawAttempt {
  termNumber?: number | null;
  attemptNumber?: number | null;
  status?: string | null;
  date?: string | null;
  timestamp?: string | null;
  note?: string | null;
}

/**
 * Attempt ordinal: canonical termNumber → legacy attemptNumber → 1-based position.
 *
 * ⚠️ Od 2026-07-28 `termNumber` znaczy NUMER TERMINU BUDYNKU (I/II/III…), a nie „które
 * pukanie do tych drzwi" — stempluje go backend przy zapisie, dane historyczne przeliczyła
 * migracja 041. Etykieta w UI brzmi więc „III termin", nie „Próba 3".
 */
export function attemptNumber(a: RawAttempt | null | undefined, index: number): number {
  const n = a?.termNumber ?? a?.attemptNumber;
  return typeof n === 'number' && Number.isFinite(n) ? n : index + 1;
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

/**
 * Rzymski numer terminu do etykiet („III termin"). Poza tablicą → arabski z kropką.
 * Sama KONWERSJA liczby; numeruje wyłącznie backend (patrz InspectionTerm w types.ts).
 */
export function romanTerm(n: number | null | undefined): string | null {
  if (typeof n !== 'number' || !Number.isInteger(n) || n < 1) return null;
  return ROMAN[n - 1] || `${n}.`;
}

/** Raw attempt date: canonical `date` ('YYYY-MM-DD') → legacy `timestamp` (ISO) → null. */
export function attemptDate(a: RawAttempt | null | undefined): string | null {
  return a?.date ?? a?.timestamp ?? null;
}

/** Sort key in ms. Missing/unparseable dates sort first (0) — stable, crash-free order. */
export function attemptSortKey(a: RawAttempt | null | undefined): number {
  const raw = attemptDate(a);
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Formatted attempt date (pl-PL, no time). '-' when missing/unparseable — never "Invalid Date". */
export function formatAttemptDate(a: RawAttempt | null | undefined): string {
  const raw = attemptDate(a);
  if (!raw) return '-';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pl-PL');
}

/** Formatted attempt time (pl-PL) — ONLY when a real ISO `timestamp` is present; else null. */
export function formatAttemptTime(a: RawAttempt | null | undefined): string | null {
  const raw = a?.timestamp;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleTimeString('pl-PL');
}
